import React, { useState, useEffect } from 'react';
import axios from '../../services/axios';
import {
  ThemeProvider, createTheme, Box, Card, CardHeader, CardContent, Grid, TextField,
  InputAdornment, IconButton, Tooltip, Fab, Avatar, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, TablePagination, Dialog, DialogTitle,
  DialogContent, DialogActions, Button, Divider, Typography, Stack,
  Snackbar, // NUEVO
  Alert,    // NUEVO
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon,
  Search as SearchIcon, Close as CloseIcon, Image as ImageIcon,
  FileUpload as UploadIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

const theme = createTheme({
  palette: {
    primary: { main: '#0B74DA' },
    background: { default: '#F5F7FA', paper: '#ffffff' },
  },
  typography: { h4: { fontWeight: 700 } },
});

const WhiteTableHead = styled(TableHead)(() => ({
  backgroundColor: '#f0f2f5',
  '& .MuiTableCell-head': { fontWeight: 700, color: '#333', fontSize: '0.9rem' },
}));

export default function Publicidads() {
  const [items, setItems] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [formOpen, setFormOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [currentIdToDelete, setCurrentIdToDelete] = useState(null);
  
  // Estado para archivo físico
  const [selectedFile, setSelectedFile] = useState(null);

  // NUEVO: Estado para notificaciones
  const [notification, setNotification] = useState({ 
    open: false, 
    message: "", 
    severity: "success" 
  });

  const [form, setForm] = useState({
    id: '', imagen: '', descripcion: '', fecha_inicio: '', fecha_fin: '',
  });

  useEffect(() => { fetchItems(); }, []);

  async function fetchItems() {
    try {
      const res = await axios.get('/publicidads');
      setItems(res.data || []);
      setFiltered(res.data || []);
    } catch (err) { console.error('Error al cargar publicidad', err); }
  }

  // --- MANEJO DE NOTIFICACIONES ---
  const handleCloseNotification = () => {
    setNotification({ ...notification, open: false });
  };

  function handleFileChange(e) {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      setForm((prev) => ({ ...prev, imagen: URL.createObjectURL(file) }));
    }
  }

  function handleInputChange(e) {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  }

  function openNew() {
    setSelectedFile(null);
    setForm({ id: '', imagen: '', descripcion: '', fecha_inicio: '', fecha_fin: '' });
    setEditing(false);
    setFormOpen(true);
  }

  function openEdit(item) {
    setSelectedFile(null);
    setForm({ ...item });
    setEditing(true);
    setFormOpen(true);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    const formData = new FormData();
    
    formData.append('descripcion', form.descripcion || '');
    formData.append('fecha_inicio', form.fecha_inicio || '');
    formData.append('fecha_fin', form.fecha_fin || '');
    formData.append('imagen', form.imagen || '');

    if (selectedFile) {
      formData.append("foto_file", selectedFile);
    }

    try {
      const config = { headers: { "Content-Type": "multipart/form-data" } };
      if (editing) {
        formData.append("_method", "PUT");
        await axios.post(`/publicidads/${form.id}`, formData, config);
      } else {
        await axios.post('/publicidads', formData, config);
      }
      
      setNotification({ open: true, message: "¡Publicidad guardada correctamente!", severity: "success" });
      fetchItems();
      setFormOpen(false);
    } catch (err) { 
      if (err.response && err.response.status === 422) {
        setNotification({ 
          open: true, 
          message: "Error: La imagen debe ser jpg, png o webp y menor a 2MB", 
          severity: "error" 
        });
      } else {
        setNotification({ open: true, message: "Error al procesar la solicitud", severity: "error" });
      }
    }
  }

  async function handleConfirmDelete() {
    try {
      await axios.delete(`/publicidads/${currentIdToDelete}`);
      setNotification({ open: true, message: "Publicidad eliminada", severity: "success" });
      fetchItems();
      setDeleteOpen(false);
    } catch (err) { 
      setNotification({ open: true, message: "No se pudo eliminar", severity: "error" });
    }
  }

  function handleSearch(e) {
    const q = e.target.value;
    setSearch(q);
    if (!q.trim()) return setFiltered(items);
    setFiltered(items.filter(i => String(i.descripcion).toLowerCase().includes(q.toLowerCase())));
  }

  const rowsToShow = filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ p: 3, bgcolor: 'background.default', minHeight: '100vh' }}>
        <Card sx={{ borderRadius: 3 }}>
          <CardHeader 
            title={<Typography variant="h4">Publicidad</Typography>} 
            action={<Fab color="primary" size="small" onClick={openNew}><AddIcon /></Fab>} 
          />
          <CardContent>
            <TextField
              fullWidth placeholder="Buscar..." value={search} onChange={handleSearch} sx={{ mb: 2 }}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
            />
            <TableContainer component={Paper}>
              <Table size="small">
                <WhiteTableHead>
                  <TableRow>
                    <TableCell>Imagen</TableCell>
                    <TableCell>Descripción</TableCell>
                    <TableCell>Inicio</TableCell>
                    <TableCell>Fin</TableCell>
                    <TableCell align="center">Acciones</TableCell>
                  </TableRow>
                </WhiteTableHead>
                <TableBody>
                  {rowsToShow.map((pub) => (
                    <TableRow key={pub.id} hover>
                      <TableCell>
                        <Avatar src={pub.imagen} variant="rounded" sx={{ width: 50, height: 50 }}><ImageIcon /></Avatar>
                      </TableCell>
                      <TableCell>{pub.descripcion}</TableCell>
                      <TableCell>{pub.fecha_inicio}</TableCell>
                      <TableCell>{pub.fecha_fin}</TableCell>
                      <TableCell align="center">
                        <IconButton onClick={() => openEdit(pub)} color="primary"><EditIcon /></IconButton>
                        <IconButton onClick={() => { setCurrentIdToDelete(pub.id); setDeleteOpen(true); }} color="error"><DeleteIcon /></IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <TablePagination
                component="div" count={filtered.length} page={page} rowsPerPage={rowsPerPage}
                onPageChange={(_, p) => setPage(p)} onRowsPerPageChange={(e) => setRowsPerPage(e.target.value)}
                labelRowsPerPage="Filas:"
              />
            </TableContainer>
          </CardContent>
        </Card>

        {/* DIALOG FORM */}
        <Dialog open={formOpen} fullWidth maxWidth="sm" onClose={() => setFormOpen(false)}>
          <DialogTitle sx={{ fontWeight: 700 }}>{editing ? 'Editar' : 'Nueva'} Publicidad</DialogTitle>
          <DialogContent dividers>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <TextField 
                  name="imagen" label="URL o Archivo" fullWidth value={form.imagen} onChange={handleInputChange}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        {/* FILTRO ACCEPT AÑADIDO */}
                        <input 
                          accept="image/jpeg,image/png,image/jpg,image/webp" 
                          type="file" 
                          id="pub-file" 
                          style={{ display: 'none' }} 
                          onChange={handleFileChange} 
                        />
                        <label htmlFor="pub-file"><IconButton component="span" color="primary"><UploadIcon /></IconButton></label>
                      </InputAdornment>
                    )
                  }}
                />
              </Grid>
              <Grid item xs={12}><TextField name="descripcion" label="Descripción" fullWidth multiline rows={2} value={form.descripcion} onChange={handleInputChange} /></Grid>
              <Grid item xs={12} md={6}><TextField name="fecha_inicio" label="Inicio" type="date" fullWidth value={form.fecha_inicio} onChange={handleInputChange} InputLabelProps={{ shrink: true }} /></Grid>
              <Grid item xs={12} md={6}><TextField name="fecha_fin" label="Fin" type="date" fullWidth value={form.fecha_fin} onChange={handleInputChange} InputLabelProps={{ shrink: true }} /></Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setFormOpen(false)}>Cancelar</Button>
            <Button variant="contained" onClick={handleSubmit} sx={{ px: 3 }}>Guardar</Button>
          </DialogActions>
        </Dialog>

        {/* NOTIFICACIÓN SNACKBAR */}
        <Snackbar 
          open={notification.open} 
          autoHideDuration={5000} 
          onClose={handleCloseNotification}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert 
            onClose={handleCloseNotification} 
            severity={notification.severity} 
            variant="filled" 
            sx={{ width: '100%' }}
          >
            {notification.message}
          </Alert>
        </Snackbar>

        {/* DELETE DIALOG */}
        <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)}>
          <DialogTitle>¿Confirmar eliminación?</DialogTitle>
          <DialogActions>
            <Button onClick={() => setDeleteOpen(false)}>No</Button>
            <Button onClick={handleConfirmDelete} color="error" variant="contained">Sí, eliminar</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </ThemeProvider>
  );
}